/**
 * Move-in & move-out, in the design's shape (COL-533).
 *
 * V2 mounted V1's `ProtocolEntry` — a large hero card with a greeting, a
 * 30px title, three bullet points and a status panel. The export draws two
 * compact rows in a beige card: a title, a one-line note, and at most one
 * button.
 *
 * Presentation only. Every piece of state comes from the SAME hooks and the
 * SAME `computeKindState` V1 uses, and starting a report writes the same URL
 * parameters, so the protocol itself is untouched — a tenant who starts here
 * lands in exactly the flow they would have from V1. `ProtocolEntry` is left
 * alone: it still serves V1, and its action-needed tests (COL-368) keep
 * passing because nothing about it changed.
 *
 * NOT ported: the design's move-out time-slot chips and "Confirm
 * appointment". We have no slot concept anywhere in the protocol code, so
 * those would be new functionality rather than a restyle — flagged rather
 * than faked with a dead control.
 */

import { useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as DatePickerCalendar } from "@/components/ui/calendar";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  getSignedUrl,
  useCreateProtocol,
  useMyMoveOutDateRequest,
  useProtocolsForBooking,
  useRequestMoveOutDate,
} from "@/hooks/useProtocol";
import { useNowTick } from "@/hooks/useNowTick";
import {
  computeKindState,
  isMoveOutScheduled,
  pickLatest,
} from "@/lib/protocol-action";
import { baseLanguageFor, localeFor } from "@/lib/i18n/languages";
import { getTenantRoomLabels } from "@/lib/tenant-room-copy";
import { useTenantKeyPickup } from "@/hooks/useTenantKeyPickup";
import {
  KeyPickupCard,
  KeyReturnCard,
} from "@/components/tenant/v2/TenantKeyCards";
import type { ProtocolKind } from "@/lib/protocol";
import { formatTenantDate } from "@/lib/date-format";
import { parseDateOnly } from "@/lib/roomAvailability";
import { openPendingDocumentTab } from "@/lib/open-document-tab";
import type { Protocol } from "@/lib/protocol";
import { getProtocolPdfCopy } from "@/lib/protocol-pdf-copy";

type Props = {
  /** Same contract as `ProtocolEntry`: hand the protocol id to the router. */
  onStart: (protocolId: string) => void;
  /*
    The SAME booking the rest of the page resolved, not a second lookup.

    This used to call `useActiveBooking()` on its own, which selects by a
    broader predicate than `TenantRoomPage`'s `useTenantBooking()` --
    includes pending applications, no archived-exclusion -- so this row
    could resolve a DIFFERENT booking than every other section on the page
    for a tenant with more than one. Key pickup/return in particular reads
    straight off this `booking`.
  */
  booking: any;
};

export default function TenantHandoverRows({ onStart, booking }: Props) {
  const { language: activeLanguage } = useLanguage();
  const language = baseLanguageFor(activeLanguage);
  const locale = localeFor(activeLanguage);
  const d = getTenantRoomLabels(language);
  const protocolPdfCopy = getProtocolPdfCopy(activeLanguage);

  const now = useNowTick(60_000);
  const {
    data: protocols = [],
    isPending: protocolsPending,
    isError: protocolsFailed,
  } = useProtocolsForBooking(booking?.id ?? null);
  /*
    A FAILED list is not an empty list.

    `data = []` on error makes `pickLatest` answer null, which reads exactly
    like "no protocol exists yet" -- so Start fell through to
    `useCreateProtocol`, which inserts unconditionally. A tenant whose list
    request failed (offline, RLS hiccup, a 500) would create a SECOND
    protocol for a booking that already had one, and the two then disagree
    about the state of the room at handover.

    The existing `busy` flag already covers the in-flight case for exactly
    this reason; the error case was the half that was missing.
  */
  const protocolsUnknown = protocolsPending || protocolsFailed;
  const create = useCreateProtocol();
  const [viewingPdfOf, setViewingPdfOf] = useState<string | null>(null);

  // Checkout protocol (Neel Slack DM 2026-10-19): the tenant may request a
  // move-out date themselves while genuinely Active and before any date is
  // confirmed — notice_given / move_out_pending / move_out_confirmed mean an
  // admin already set one through the normal flow.
  const { data: moveOutRequest } = useMyMoveOutDateRequest(booking?.id ?? null);
  const requestMoveOut = useRequestMoveOutDate();
  const [requestMoveOutDate, setRequestMoveOutDate] = useState<
    Date | undefined
  >(undefined);
  const [requestMoveOutPopoverOpen, setRequestMoveOutPopoverOpen] =
    useState(false);
  const canRequestMoveOut =
    booking?.status === "active" &&
    !isMoveOutScheduled(booking) &&
    !moveOutRequest;

  const submitMoveOutRequest = async () => {
    if (!booking?.id || !requestMoveOutDate) return;
    try {
      await requestMoveOut.mutateAsync({
        bookingId: booking.id,
        requestedDate: format(requestMoveOutDate, "yyyy-MM-dd"),
      });
      toast.success(d.requestMoveOutDateSuccess);
      setRequestMoveOutPopoverOpen(false);
      setRequestMoveOutDate(undefined);
    } catch (err: any) {
      toast.error(err?.message ?? d.requestMoveOutDateError);
    }
  };

  const moveIn = pickLatest(protocols, "move_in");
  // Key pickup/return details for this same booking.
  const keyPickup = useTenantKeyPickup(booking);
  const moveOut = pickLatest(protocols, "move_out");
  const moveInState = computeKindState(moveIn, booking, "move_in", now);
  const moveOutState = computeKindState(moveOut, booking, "move_out", now);

  /*
    There is nothing to hand over before there is a move-out (Neel, 23 Oct).

    `computeMoveOutWindowStatus` answers "open" when the booking carries NO
    move-out date at all -- no `opensAt`, no `closesAt`, nothing to be before
    or after -- so `computeKindState` returned `not_started`, unlocked, and
    this row offered "Start report" to a tenant who had not even moved in
    yet. The window math is right for a booking that HAS a date; what was
    missing is the question that comes before it.

    Both halves of Neel's rule: the tenant has to have moved in, and the
    move-out has to be CONFIRMED -- `move_out_date` doubles as the contract
    end on an auto-prolonging tenancy, which is the same trap `moveOutNote`
    and `KeyReturnCard` below already guard against.

    Two things still get through: a draft the tenant opened themselves, and a
    window an admin reopened, which is an explicit request for exactly this
    walkthrough.
  */
  /*
    `parseDateOnly`, not hand-split digits: it is the shared reader for these
    date-only columns and it tolerates a value that arrives with a time part,
    which a `split("-")` does not -- that would read as "not moved in" and
    keep Start blocked for a tenant who has (#2039 review).

    `move_in_date ?? start_date`, as every other reader of this booking does
    (`stayProgress`, the overview's own upcoming-move-in check). Reading only
    `move_in_date` made a booking that carries its date in `start_date` look
    like nobody had moved in, which blocked the report for the whole tenancy
    (Neel, 26 Oct).
  */
  const movedIn = (() => {
    const raw = String(booking?.move_in_date ?? booking?.start_date ?? "");
    if (!raw) return false;
    const parsed = parseDateOnly(raw);
    if (Number.isNaN(parsed.getTime())) return false;
    const today = new Date(now);
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    ).getTime();
    return parsed.getTime() <= startOfToday;
  })();
  const moveOutStartable =
    moveOutState.kindStatus === "draft" ||
    moveOutState.windowState === "reopened" ||
    (movedIn && isMoveOutScheduled(booking));

  const day = (iso: string | null | undefined) =>
    iso ? formatTenantDate(iso) : "—";

  const start = async (
    kind: ProtocolKind,
    existing: { id: string; status?: string } | null
  ) => {
    /*
      Reuse an id ONLY for a draft, exactly as V1 does.

      After an admin reopens an auto-accepted protocol, `computeKindState`
      answers `not_started` while `pickLatest` still returns the SUBMITTED
      row. Handing that id to the router sends the tenant to `done` — a
      read-only summary — so the reopened protocol could never be filled in.
    */
    if (existing && String(existing.status ?? "") === "draft") {
      onStart(existing.id);
      return;
    }
    if (!booking?.id) return;
    /*
      Never create while the list is unknown. The button is disabled in that
      state, but this is the guard that actually matters -- the disable is a
      UI affordance and this is the one thing standing between a failed
      fetch and a duplicate protocol.
    */
    if (protocolsUnknown) {
      toast.error(d.documentUnavailable);
      return;
    }
    try {
      const protocol = await create.mutateAsync({
        bookingId: booking.id,
        kind,
      });
      onStart(protocol.id);
    } catch (err: any) {
      toast.error(err?.message ?? d.documentUnavailable);
    }
  };

  /**
   * Open a submitted protocol's stored PDF directly from the dashboard.
   *
   * The previous handler reused `onStart`, which routes submitted protocols
   * to the wizard's Done step. That made "Report PDF" a two-step flow: first
   * a confirmation screen, then a separate download button. Open the tab
   * synchronously inside the click gesture so browsers do not block it while
   * the signed URL is being fetched.
   */
  const viewPdf = async (protocol: Protocol) => {
    if (!protocol.pdf_storage_path) {
      onStart(protocol.id);
      return;
    }
    if (viewingPdfOf) return;

    const tab = openPendingDocumentTab();
    setViewingPdfOf(protocol.id);
    try {
      const url = await getSignedUrl(protocol.pdf_storage_path);
      if (!url) throw new Error("could not sign the protocol pdf");
      tab.navigate(url);
    } catch {
      tab.cancel();
      toast.error(protocolPdfCopy.viewPdfFailed);
    } finally {
      setViewingPdfOf(null);
    }
  };

  /*
    `locked` is checked BEFORE kindStatus. Before the window opens
    `computeKindState` answers `not_started` with `locked: true`, so
    switching on status alone showed "document your room now" under a row
    with no button — and the locked branch was unreachable.

    `closed` is kept distinct from `expired`: an admin re-lock is a neutral
    close, NOT deemed acceptance, and saying the room is deemed accepted
    when it is not is a statement about the tenant's legal position.
  */
  const moveInNote = (() => {
    const date = day(booking?.move_in_date);
    const from = day(moveInState.availableFrom);
    if (moveInState.kindStatus === "submitted")
      return d.moveInDone.replace("{date}", date);
    if (moveInState.kindStatus === "expired")
      return d.moveInExpired.replace("{date}", date);
    if (moveInState.kindStatus === "closed")
      return d.moveInClosed.replace("{date}", date);
    if (moveInState.locked)
      return d.moveInLocked.replace("{date}", date).replace("{from}", from);
    return d.moveInReady.replace("{date}", date);
  })();

  const moveOutNote = (() => {
    /*
      No confirmed move-out: say so, rather than dating one.

      `useActiveBooking` projects `move_out_date ?? end_date`, so an ongoing
      tenancy carries its CONTRACT END in that field -- and this row used to
      present it as "your move-out is on <date>". Blanking the date made it
      honest but left "Move-out — · unlocked from —", which reads like
      something failed to load. Neel asked for the real line (10 Oct).

      Checked before the window states but AFTER `submitted`: a submitted
      protocol is evidence a handover actually happened, and that should not
      be relabelled as "nothing scheduled" on the strength of a flag.
    */
    // A pending checkout-protocol request takes priority over "no move-out
    // scheduled" — it isn't that nothing is scheduled, it's that nothing is
    // CONFIRMED yet.
    if (moveOutRequest)
      return d.requestMoveOutDateRequestedNote
        .replace("{date}", day(moveOutRequest.requested_date))
        .replace("{sentDate}", day(moveOutRequest.created_at));
    /*
      The SAME predicate the button uses (Neel, 26 Oct).

      This read `!booking?.move_out_confirmed`, and that field is not a column
      on `bookings` at all -- `useTenantBooking` selects `*`, so it arrives
      `undefined` here and this branch always won. With the button now opening
      on a real date, the row would otherwise offer "Start report" underneath
      the words "No move-out scheduled", which is the contradiction Neel
      photographed on 23 Oct wearing the opposite hat.
    */
    if (!isMoveOutScheduled(booking) && moveOutState.kindStatus !== "submitted")
      return canRequestMoveOut
        ? d.requestMoveOutDateRowHint
        : d.moveOutNotScheduled;

    const date = day(booking?.move_out_date);
    const from = day(moveOutState.availableFrom);
    if (moveOutState.kindStatus === "submitted")
      return d.moveOutDone.replace("{date}", date);
    if (moveOutState.kindStatus === "expired")
      return d.moveOutExpired.replace("{date}", date);
    if (moveOutState.kindStatus === "closed")
      return d.moveOutClosed.replace("{date}", date);
    if (moveOutState.locked)
      return d.moveOutLocked.replace("{date}", date).replace("{from}", from);
    return d.moveOutReady.replace("{date}", date);
  })();

  return (
    <Card className="rounded-[20px] border-0 bg-muted px-7 py-1.5">
      <Row
        title={d.moveInReport}
        note={moveInNote}
        state={moveInState}
        startLabel={
          moveInState.kindStatus === "draft" ? d.resumeReport : d.startReport
        }
        pdfLabel={d.reportPdf}
        busy={protocolsUnknown || create.isPending || !!viewingPdfOf}
        onStart={() => void start("move_in", moveIn ?? null)}
        onPdf={moveIn ? () => void viewPdf(moveIn) : undefined}
      />
      {/*
        Key pickup sits UNDER the move-in row, and key return under move-out,
        because that is the row each belongs to. This is the information the
        old standalone check-in screen carried that nothing else did; with it
        here, that screen is a duplicate and is no longer mounted.
      */}
      <KeyPickupCard
        booking={booking}
        pickup={keyPickup}
        d={d as any}
        locale={locale}
      />
      <Row
        title={d.moveOutReport}
        note={moveOutNote}
        state={moveOutState}
        startLabel={
          moveOutState.kindStatus === "draft" ? d.resumeReport : d.startReport
        }
        pdfLabel={d.reportPdf}
        busy={protocolsUnknown || create.isPending || !!viewingPdfOf}
        startable={moveOutStartable}
        onStart={() => void start("move_out", moveOut ?? null)}
        onPdf={moveOut ? () => void viewPdf(moveOut) : undefined}
        requestMoveOutButton={
          canRequestMoveOut ? (
            <RequestMoveOutPopover
              d={d}
              language={language}
              earliestDate={booking?.end_date ?? null}
              selected={requestMoveOutDate}
              onSelect={setRequestMoveOutDate}
              open={requestMoveOutPopoverOpen}
              onOpenChange={(open) => {
                setRequestMoveOutPopoverOpen(open);
                if (!open) setRequestMoveOutDate(undefined);
              }}
              onSubmit={() => void submitMoveOutRequest()}
              submitting={requestMoveOut.isPending}
            />
          ) : undefined
        }
        pendingPill={
          moveOutRequest ? d.requestMoveOutDateAwaitingPill : undefined
        }
      />
      <KeyReturnCard
        /*
          A CONFIRMED move-out only. `move_out_date` doubles as the contract
          end for an ongoing tenancy, so treating it as scheduled would tell
          someone still living there to hand their keys back -- the same trap
          `moveOutNote` above already guards against.
        */
        moveOutConfirmed={isMoveOutScheduled(booking)}
        /*
          No return-instructions field exists anywhere in the schema -- only
          `key_return_pending`/`key_returned_at` timestamps/flags. This used
          to pass the PICKUP instructions here instead, so a return card
          could show "collect the key from the lockbox at..." as if it were
          the return procedure. `null` falls through to the card's own
          generic `keyReturnDefaultHint` ("return the keys to the same place
          you collected them"), which is actually true for every booking.
        */
        instructions={null}
        d={d as any}
      />
    </Card>
  );
}

function Row({
  title,
  note,
  state,
  startLabel,
  pdfLabel,
  busy,
  startable = true,
  onStart,
  onPdf,
  requestMoveOutButton,
  pendingPill,
}: {
  title: string;
  note: string;
  state: { kindStatus: string; locked: boolean };
  startLabel: string;
  pdfLabel: string;
  /*
    Disabled while the protocol list is loading or a create is in flight.
    `useCreateProtocol` inserts unconditionally, so a double press before
    the first insert lands would create two drafts for one booking.
  */
  busy: boolean;
  /**
   * Whether this kind may be started AT ALL right now — a precondition the
   * window math cannot answer. Defaults to true; move-out passes false until
   * the tenant has moved in and a move-out date is confirmed.
   */
  startable?: boolean;
  onStart: () => void;
  onPdf?: () => void;
  /** move_out only: set while the tenant may still request a date — takes
   *  the row's one button slot ahead of Start/Resume/View (there is nothing
   *  to hand over before a date is even confirmed). */
  requestMoveOutButton?: React.ReactNode;
  /** move_out only: a pending request's "Awaiting approval" pill, taking
   *  the row's one slot ahead of everything else. */
  pendingPill?: string;
}) {
  const canStart =
    startable &&
    !state.locked &&
    (state.kindStatus === "not_started" || state.kindStatus === "draft");
  const submitted = state.kindStatus === "submitted";

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border-t border-input py-4 first:border-t-0">
      <div className="min-w-0">
        <p className="font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{note}</p>
      </div>
      {pendingPill ? (
        <span className="shrink-0 whitespace-nowrap rounded-full bg-[rgba(138,107,84,0.12)] px-3 py-1.5 text-[13px] font-semibold text-[#8A6B54]">
          {pendingPill}
        </span>
      ) : requestMoveOutButton ? (
        requestMoveOutButton
      ) : canStart ? (
        <Button
          className="shrink-0 rounded-full px-5"
          disabled={busy}
          onClick={onStart}
        >
          {startLabel}
        </Button>
      ) : submitted && onPdf ? (
        <Button
          variant="secondary"
          className="shrink-0 rounded-full bg-card px-5 hover:bg-card/80"
          disabled={busy}
          onClick={onPdf}
        >
          {pdfLabel}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * "Request check-out date" — a button that opens the calendar directly in a
 * popover anchored to itself (per the approved mockup), not a separate
 * modal. Picking a date and submitting both happen inline in the popover.
 */
function RequestMoveOutPopover({
  d,
  language,
  earliestDate,
  selected,
  onSelect,
  open,
  onOpenChange,
  onSubmit,
  submitting,
}: {
  d: ReturnType<typeof getTenantRoomLabels>;
  language: "en" | "de";
  /** `booking.end_date` — the contract's earliest possible move-out. */
  earliestDate: string | null;
  selected: Date | undefined;
  onSelect: (date: Date | undefined) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => void;
  submitting: boolean;
}) {
  const earliest = earliestDate ? new Date(earliestDate) : null;
  const locale = language === "de" ? "de-DE" : "en-GB";
  const selectedLabel = selected
    ? selected.toLocaleDateString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button className="shrink-0 rounded-full px-5">
          {d.requestMoveOutDate}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[296px] p-3.5">
        <DatePickerCalendar
          mode="single"
          selected={selected}
          onSelect={onSelect}
          disabled={earliest ? { before: earliest } : undefined}
          defaultMonth={selected ?? earliest ?? undefined}
          initialFocus
        />
        {earliest && (
          <p className="mt-2 px-0.5 text-xs text-muted-foreground">
            {d.requestMoveOutDateEarliest.replace(
              "{date}",
              earliest.toLocaleDateString(locale, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            )}
          </p>
        )}
        <div className="mt-3 border-t border-border pt-3">
          <div className="pl-0.5">
            <div className="text-xs text-muted-foreground">
              {d.requestMoveOutDatePopoverLabel}
            </div>
            <div className="text-sm font-semibold">{selectedLabel}</div>
          </div>
          <Button
            size="sm"
            className="mt-2 w-full rounded-full"
            disabled={!selected || submitting}
            onClick={onSubmit}
          >
            {d.requestMoveOutDateSubmit}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
